import React, { useMemo } from 'react';

import { FormField } from '@xcritical/forms';
import Button from '@xcritical/button';
import { Popover } from '@xcritical/popover';

import { Spacing } from '@crm-framework/spacing';
import { useRegistry } from '@crm-framework/registry';
import { InputFormField, TextareaFormField } from '@crm-framework/form-fields';
import { Label } from '@crm-framework/label';
import { RequiredPosition } from '@crm-framework/label/src/types';
import { ITabsProps, Tabs } from '@crm-framework/tabs';

import { ITabFieldsProps, IDescriptionTabProps } from '../../types';
import { PROMO_REGISTRIES } from '../../consts';

import { ButtonsWrapper } from './styled';
import { HelpPlaceholders } from './HelpPlaceholders';
import { PreviewModal } from './PreviewModal';

const EmailTabFields = ({
  index,
  isRequired,
  disabled,
  languageName,
}: ITabFieldsProps) => {
  const path = `texts.${index}`;
  const emailBodyPath = `${path}.emailBody`;
  const emailSubjectPath = `${path}.emailSubject`;
  const emailFromPath = `${path}.emailFrom`;

  return (
    <>
      <Spacing mb={20}>
        <Spacing mb={10}>
          <Label
            lineHeight="0px"
            htmlFor={emailBodyPath}
            isRequired={isRequired}>
            Description
          </Label>
        </Spacing>
        <FormField
          id={emailBodyPath}
          name={emailBodyPath}
          component={TextareaFormField}
          disabled={disabled}
        />
        <ButtonsWrapper>
          <Popover
            position="top center"
            appearance="promoEmailPlaceholders"
            content={<HelpPlaceholders />}>
            <Button appearance="link">Help</Button>
          </Popover>
          <PreviewModal path={emailBodyPath} languageName={languageName!} />
        </ButtonsWrapper>
      </Spacing>

      <Spacing mb={20}>
        <Spacing mb={10}>
          <Label lineHeight="0px" htmlFor={emailSubjectPath}>
            Subject
          </Label>
        </Spacing>
        <FormField
          id={emailSubjectPath}
          shouldFitContainer
          name={emailSubjectPath}
          component={InputFormField}
          disabled={disabled}
        />
      </Spacing>

      <Spacing mb={20}>
        <Spacing mb={10}>
          <Label lineHeight="0px" htmlFor={emailFromPath}>
            From
          </Label>
        </Spacing>
        <FormField
          id={emailFromPath}
          shouldFitContainer
          name={emailFromPath}
          component={InputFormField}
          disabled={disabled}
        />
      </Spacing>
    </>
  );
};

export const EmailTabs = ({ texts, disabled }: IDescriptionTabProps) => {
  const activeTab = useRegistry<ITabsProps['activeTab']>(
    PROMO_REGISTRIES.ACTIVE_LANG
  );
  const mappedTexts = useMemo(
    () =>
      texts.map(({ languageName }, index: number) => {
        const isRequired = languageName === 'English';

        return {
          label: () => (
            <Label
              lineHeight="0px"
              requiredPosition={RequiredPosition.left}
              isRequired={isRequired}>
              {languageName}
            </Label>
          ),
          component: () => (
            <EmailTabFields
              disabled={disabled}
              isRequired={isRequired}
              index={index}
              languageName={languageName}
            />
          ),
        };
      }),
    [texts]
  );

  return (
    <Tabs activeTab={activeTab} items={mappedTexts} appearance="vertical" />
  );
};
